<template>
  <div v-if="detailInfo && !showIframe" class="follow-up-detail">
    <div class="title">{{ detailInfo.taskName }}</div>
    <div class="item-header">
      <div class="status-lbl" :class="FollowUpStatusEnum[detailInfo.planStatus]">
        {{ FOLLOW_UP_STATUS_TYPE.find((s) => s.key === detailInfo?.planStatus)?.label ?? '' }}
      </div>
      <div class="item-time">
        <span>截止时间</span>
        {{ detailInfo.endTime }}
      </div>
    </div>
    <div class="detail-content">
      {{ detailInfo.content.notify?.content ?? '' }}
    </div>
    <div class="detail-attach">
      <div class="imgs-wrap">
        <img v-for="(imgUrl, index) in imgs" :key="`img${index}`" :src="imgUrl" alt="" />
      </div>
      <div class="files-wrap">
        <div v-for="(file, index) in appendix" :key="`file${index}`" class="file-item" @click="handleClickFile(file)">
          <img :src="getFileTypeIcon(file.type)" alt="" class="img-file-list" />
          <div>{{ file.name }}</div>
        </div>
      </div>
    </div>
  </div>
  <div v-if="showIframe" class="file-iframe">
    <div class="tool-bar">
      <div class="close-btn" @click="handleCloseIframe">关闭</div>
    </div>
    <iframe :src="currentFileUrl" frameborder="0"></iframe>
  </div>
</template>

<script setup lang="ts">
import { useFollowUpStore } from '@/store/modules/followUp';
import { router } from '@/router';
import { FOLLOW_UP_STATUS_TYPE, FOLLOW_UP_TASK_TYPE } from '@/constants/global';
import { FollowUpStatusEnum } from '@/enums/CommonEnum';
import { ResLPlanDetail } from '@tencent/fd-types/patient';
import PictureIcon from '@/assets/images/icon-picture.png';
import PDFIcon from '@/assets/images/icon-pdf.png';
import VideoIcon from '@/assets/images/icon-video.png';
import WordIcon from '@/assets/images/icon-word.png';
import LinkIcon from '@/assets/images/icon-link.png';
import FileIcon from '@/assets/images/icon-file.png';
import { showConfirmDialog, showToast } from 'vant';

const followUpStore = useFollowUpStore();
const detailInfo = ref<ResLPlanDetail | null>(null);
const showIframe = ref(false);
const currentFileUrl = ref('');
const imgs = computed(() => {
  return detailInfo.value?.sourceMaterialFileInfos
    .filter((s) => {
      return s.type.indexOf('image') > -1;
    })
    ?.map((s) => s.url);
});
const appendix = computed(() => {
  const files =
    detailInfo.value?.sourceMaterialFileInfos
      .filter((s) => {
        return s.type.indexOf('image') < 0;
      })
      ?.map((s) => {
        return {
          url: s.url,
          name: s.name,
          type: s.type,
        };
      }) || [];
  const links =
    detailInfo.value?.content.notify?.appendixLink.map((s) => ({
      url: s.url,
      name: s.title,
      type: 'link',
    })) || [];
  return [...files, ...links];
});
const getFileTypeIcon = (fileType) => {
  if (fileType.includes('image')) return PictureIcon;
  if (fileType === 'application/pdf') return PDFIcon;
  if (fileType.includes('word')) return WordIcon;
  if (fileType.includes('video')) return VideoIcon;
  if (fileType === 'link') return LinkIcon;
  return FileIcon;
};

const handleClickFile = (file) => {
  if (!file.url) return;
  if (
    file.type.includes('pdf') ||
    file.type === 'text/plain' ||
    file.type.includes('sheet') ||
    file.type.includes('document')
  ) {
    window.open(file.url);
  } else {
    showConfirmDialog({
      title: '复制链接',
      message: '复制链接到剪贴板，粘贴内容到外部浏览器中打开',
      confirmButtonText: '复制',
    })
      .then(() => {
        navigator.clipboard.writeText(file.url).then(() => {
          showToast('已复制');
        });
      })
      .catch(() => {
        // on cancel
      });
  }
};

const handleCloseIframe = () => {
  if (detailInfo.value?.taskType === 'SF') {
    router.go(-1);
  } else {
    showIframe.value = false;
  }
};

const setUp = async () => {
  const { planId, profileId } = router.currentRoute.value.query;
  if (planId) {
    const res = await followUpStore.getFollowUpPlanDetail({ planId, profileId });
    detailInfo.value = res;
  }
};
setUp();
</script>

<style scoped lang="less">
.follow-up-detail {
  position: relative;
  padding: 32px 40px;
  background: #fff;
  text-align: left;

  .title {
    margin-bottom: 24px;
    font-size: 32px;
    color: #16181a;
  }

  .item-header {
    position: relative;
    margin-bottom: 40px;

    .status-lbl {
      padding: 2px 8px;
      border-radius: 4px;
      width: 88px;
      height: 40px;
      line-height: 40px;
      font-style: normal;
      font-size: 24px;
    }

    .sending {
      background: #e6faf1;
      color: #0ac76f;
    }

    .waiting {
      background: #e6faf1;
      color: #0ac76f;
    }

    .done {
      background: #e6faf1;
      color: #0ac76f;
    }

    .item-time {
      display: inline-block;
      position: absolute;
      left: 108px;
      top: 0;
      padding: 1px;
      height: 40px;
      line-height: 40px;
      font-size: 24px;
      color: #848e99;
    }
  }

  .detail-content {
    margin-bottom: 28px;
    font-size: 28px;
    color: #16181a;
  }

  .detail-attach {
    .imgs-wrap {
      padding-bottom: 40px;
      border-bottom: 1px solid #d9d9d9;

      > img {
        border: 1px solid #dce1e5;
        max-width: 670px;
      }
    }

    .files-wrap {
      .file-item {
        display: flex;
        margin-top: 24px;
        padding: 24px;
        background: #edf0f2;

        > img {
          width: 60px;
          height: 60px;
        }

        > div {
          overflow: hidden;
          margin-left: 16px;
          border-radius: 8px;
          max-width: 500px;
          height: 60px;
          line-height: 60px;
          text-overflow: ellipsis;
          font-size: 28px;
          color: #1c2028;
          white-space: nowrap;
        }
      }
    }
  }
}

.file-iframe {
  position: relative;
  width: 100vw;
  height: 100vh;

  .tool-bar {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    padding: 10px;
    width: 100%;
    height: 60px;
    background: transparent;
    text-align: right;

    .close-btn {
      padding-top: 6px;
      border-radius: 30px;
      width: 80px;
      height: 40px;
      background-color: #5054546b;
      text-align: center;
      font-size: 28px;
      color: rgb(103, 99, 99);
    }
  }

  > iframe {
    width: 100%;
    height: 100%;
  }
}
</style>
